import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ShieldCheck, RefreshCw } from "lucide-react";
import { API } from "../../config";
import { useAuth } from "../../AuthContext";

const ACTION_STYLE = {
  "beta_pro.grant":      { label: "Beta PRO granted",  tone: "emerald" },
  "beta_pro.revoke":     { label: "Beta PRO revoked",  tone: "rose" },
  "mcp_key.tier_change": { label: "Key tier changed",  tone: "cyan" },
  "user.update":         { label: "User updated",      tone: "cyan" },
  "user.delete":         { label: "User deleted",      tone: "rose" },
  "api_key.create":      { label: "API key created",   tone: "emerald" },
  "api_key.revoke":      { label: "API key revoked",   tone: "rose" },
  "secret.update":       { label: "Secret updated",    tone: "amber" },
  "secret.delete":       { label: "Secret deleted",    tone: "rose" },
  "beta_spots.change":   { label: "Beta spots changed", tone: "cyan" },
};

const TONE = {
  emerald: (d) => d ? "bg-emerald-400/15 text-emerald-300" : "bg-emerald-100 text-emerald-700",
  rose:    (d) => d ? "bg-rose-400/15 text-rose-300" : "bg-rose-100 text-rose-700",
  cyan:    (d) => d ? "bg-cyan-400/15 text-cyan-300" : "bg-cyan-100 text-cyan-700",
  amber:   (d) => d ? "bg-amber-400/15 text-amber-300" : "bg-amber-100 text-amber-700",
  slate:   (d) => d ? "bg-slate-700 text-slate-300" : "bg-slate-200 text-slate-600",
};

export default function AdminAuditCard({ dm }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const { authHeader } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/audit-log?limit=100`, { headers: authHeader() });
      setRows(data.entries || []);
      setTotal(data.total || 0);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const fmtMeta = (m) => {
    if (!m || Object.keys(m).length === 0) return "";
    if (m.from && m.to) return `${m.from} → ${m.to}`;
    if (m.keys) return (m.keys || []).join(", ");
    return Object.entries(m).map(([k, v]) => `${k}: ${v}`).join(" · ");
  };

  return (
    <div data-testid="admin-audit-card" className={`mt-6 rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <ShieldCheck className={`w-4 h-4 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
          <h3 className={`editorial-heading text-xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Admin Audit Log</h3>
        </div>
        <button onClick={load} data-testid="admin-audit-refresh" className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-colors ${dm ? "text-slate-400 hover:text-slate-200" : "text-[#6b7a7d] hover:text-[#191c1d]"}`}>
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      <p className={`text-xs mb-4 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Sensitive actions — grants, revokes, tier & role changes, key/secret mutations. {total} total.</p>

      {loading ? (
        <p className={`text-sm text-center py-6 ${dm ? "text-slate-600" : "text-[#6b7a7d]"}`}>Loading…</p>
      ) : rows.length === 0 ? (
        <p className={`text-sm text-center py-6 ${dm ? "text-slate-600" : "text-[#6b7a7d]"}`}>No admin actions recorded yet</p>
      ) : (
        <div className="space-y-1 max-h-[420px] overflow-y-auto">
          {rows.map((e, i) => {
            const s = ACTION_STYLE[e.action] || { label: e.action, tone: "slate" };
            return (
              <div key={e.id || i} data-testid={`audit-row-${i}`} className={`flex items-start gap-3 py-2.5 border-b ${dm ? "border-white/4" : "border-black/4"} last:border-0`}>
                <span className={`category-tag flex-shrink-0 pt-0.5 w-28 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{e.timestamp ? new Date(e.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${TONE[s.tone](dm)}`}>{s.label}</span>
                <div className="min-w-0">
                  <p className={`text-sm font-medium truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{e.target || "—"}{fmtMeta(e.meta) && <span className={`ml-2 font-normal ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>({fmtMeta(e.meta)})</span>}</p>
                  <p className={`category-tag mt-0.5 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>by {e.actor}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
